import React from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, LogIn, Send, Music2 } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import Navigation from "./Navigation";
import { useAuth } from "@/context/AuthContext";

function UserBlock({ user, onLogout, onLogin }) {
  return (
    <div className="border-t border-slate-200 pt-5">
      {user ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-100 grid place-items-center font-display font-semibold text-slate-700">
              {(user.name || user.email || "U").charAt(0).toUpperCase()}
            </div>
            <div className="leading-tight min-w-0">
              <div className="font-display font-semibold text-sm truncate">{user.name || user.email}</div>
              <div className="text-[10px] tracking-widest uppercase font-mono-plex text-slate-400">
                {user.role === "admin" ? "ADMIN" : user.status?.toUpperCase() || "USER"}
              </div>
            </div>
          </div>
          <button
            data-testid="sidebar-logout-btn"
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 text-sm py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      ) : (
        <button
          data-testid="sidebar-login-btn"
          onClick={onLogin}
          className="w-full flex items-center justify-center gap-2 text-sm py-2.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors"
        >
          <LogIn size={14} /> Log in
        </button>
      )}
    </div>
  );
}

function CommunityBlock() {
  return (
    <div className="mt-6 mb-6 flex items-center justify-center gap-3">
      <a
        data-testid="sidebar-telegram-link"
        href="https://t.me/tradingsecretmalaysia"
        target="_blank"
        rel="noreferrer"
        aria-label="Join Telegram"
        title="Join Telegram"
        className="w-10 h-10 rounded-xl bg-[#229ED9] grid place-items-center text-white hover:scale-110"
        style={{ transition: "transform 0.2s ease" }}
      >
        <Send size={16} className="translate-x-[1px] -translate-y-[1px]" />
      </a>
      <a
        data-testid="sidebar-tiktok-link"
        href="https://www.tiktok.com/@nabilguruforex"
        target="_blank"
        rel="noreferrer"
        aria-label="Follow TikTok"
        title="Follow TikTok"
        className="w-10 h-10 rounded-xl bg-slate-900 grid place-items-center text-white hover:scale-110"
        style={{ transition: "transform 0.2s ease" }}
      >
        <Music2 size={16} />
      </a>
    </div>
  );
}

/**
 * Desktop-only sidebar (≥1024px). On mobile / tablet, navigation is handled
 * by the persistent <BottomNav />.
 */
export default function Sidebar({ user }) {
  const nav = useNavigate();
  const { logout } = useAuth();

  return (
    <aside
      className="hidden lg:block border-r border-slate-200 bg-white lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto"
      data-testid="sidebar"
    >
      <div className="px-6 pt-8 pb-6">
        <BrandLogo />
      </div>

      <Navigation isAdmin={user?.role === "admin"} />

      <div className="px-5 mt-4">
        <UserBlock
          user={user}
          onLogout={async () => { await logout(); nav("/"); }}
          onLogin={() => nav("/login")}
        />
        <CommunityBlock />
      </div>
    </aside>
  );
}
